import React, { useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { useData } from "../../context/DataContext";
import { TrainingQualificationRegister } from "../../components/TrainingQualificationRegister";
import { QualificationsListView } from "../../components/qualifications/QualificationsListView";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "../../components/ui/tabs";
import { LayoutDashboard, Settings, ChevronDown, List, Plus, Archive, MoreHorizontal } from "lucide-react";
import { 
    DropdownMenu, 
    DropdownMenuContent, 
    DropdownMenuItem, 
    DropdownMenuTrigger 
} from "../../components/ui/dropdown-menu";
import { Button } from "../../components/ui/button";
import { Card, CardContent } from "../../components/ui/card";
import { Can } from "../../components/Can";
import { ACTIONS, RESOURCES } from "../../constants/permissions.constants";
import { calculateQualificationStatus } from "../../utils/qualification-utils";
import { Qualification } from "../../types";

const QualificationRegisterPage: React.FC = () => {
    const navigate = useNavigate();
    const { qualifications } = useData();
    const [activeTab, setActiveTab] = useState("dashboard");

    // Summary calculations for the dashboard
    const stats = useMemo(() => {
        // Filter out soft-deleted qualifications
        const activeQualifications = qualifications.filter((q: Qualification) => !q.deleted);
        
        return {
            total: activeQualifications.length,
            active: activeQualifications.filter(q => calculateQualificationStatus(q) === "Active").length,
            expiringSoon: activeQualifications.filter(q => calculateQualificationStatus(q) === "Expiring Soon").length,
            expired: activeQualifications.filter(q => calculateQualificationStatus(q) === "Expired").length
        };
    }, [qualifications]);

    return (
        <div className="container mx-auto px-4 py-8">
            {/* Header section */}
            <div className="flex justify-between items-center mb-8">
                <div>
                    <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Qualifications Register</h1>
                    <p className="text-slate-500 text-sm mt-1">
                        Organization-wide tracking of staff training, licenses, and certifications
                    </p>
                </div>
                <div className="flex items-center gap-2">
                    {/* Desktop Actions */}
                    <div className="hidden md:flex items-center gap-2">
                        <Can I={ACTIONS.READ} a={RESOURCES.QUALIFICATION}>
                            <Button 
                                variant="outline" 
                                className="bg-white border-slate-200 text-slate-700 h-10 px-4 font-semibold shadow-sm hover:bg-slate-50 transition-all"
                                onClick={() => navigate("/qualifications/recycle-bin")}
                            >
                                <Archive className="h-4 w-4 mr-2" />
                                Recycle Bin
                            </Button>
                        </Can>

                        <Can I={ACTIONS.CREATE} a={RESOURCES.QUALIFICATION}>
                            <Button 
                                className="bg-slate-900 hover:bg-slate-800 text-white h-10 px-4 font-semibold shadow-sm transition-all"
                                onClick={() => navigate("/qualifications/add")}
                            >
                                <Plus className="h-4 w-4 mr-2" />
                                Add Qualification
                            </Button>
                        </Can>

                        <Can I={ACTIONS.READ} a={RESOURCES.QUALIFICATION_SETTINGS}>
                            <Button 
                                variant="outline" 
                                size="icon"
                                className="bg-white border-slate-200 text-slate-700 h-10 w-10 font-semibold shadow-sm hover:bg-slate-50 transition-all"
                                onClick={() => navigate("/qualifications/settings")}
                                title="Settings"
                            >
                                <Settings className="h-4 w-4" />
                            </Button>
                        </Can>
                    </div>

                    {/* Mobile Actions Dropdown */}
                    <div className="md:hidden">
                        <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                                <Button variant="outline" size="icon" className="h-10 w-10 border-slate-200">
                                    <MoreHorizontal className="h-5 w-5 text-slate-600" />
                                </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end" className="w-56 p-2 space-y-1">
                                <Can I={ACTIONS.CREATE} a={RESOURCES.QUALIFICATION}>
                                    <DropdownMenuItem onClick={() => navigate("/qualifications/add")} className="rounded-lg h-11 font-bold gap-3">
                                        <Plus className="h-4 w-4 text-blue-600" />
                                        Add Qualification
                                    </DropdownMenuItem>
                                </Can>
                                <Can I={ACTIONS.READ} a={RESOURCES.QUALIFICATION}>
                                    <DropdownMenuItem onClick={() => navigate("/qualifications/recycle-bin")} className="rounded-lg h-11 font-bold gap-3">
                                        <Archive className="h-4 w-4 text-slate-600" />
                                        Recycle Bin
                                    </DropdownMenuItem>
                                </Can>
                                <Can I={ACTIONS.READ} a={RESOURCES.QUALIFICATION_SETTINGS}>
                                    <DropdownMenuItem onClick={() => navigate("/qualifications/settings")} className="rounded-lg h-11 font-bold gap-3">
                                        <Settings className="h-4 w-4 text-slate-600" />
                                        Settings
                                    </DropdownMenuItem>
                                </Can>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </div>
                </div>
            </div>

            <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                {/* Desktop Tab List */}
                <div className="hidden md:block w-full overflow-x-auto pb-1 no-scrollbar">
                    <TabsList className="bg-gray-100 p-1 rounded-full min-w-max md:w-fit flex items-center h-auto mb-6">
                        <TabsTrigger 
                            value="dashboard" 
                            className="rounded-full px-4 md:px-6 py-2 flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-600 font-medium transition-all whitespace-nowrap"
                        >
                            <LayoutDashboard size={18} />
                            <span className="text-sm md:text-base">Dashboard</span>
                        </TabsTrigger>
                        <TabsTrigger 
                            value="register" 
                            className="rounded-full px-4 md:px-6 py-2 flex items-center gap-2 data-[state=active]:bg-white data-[state=active]:text-blue-600 font-medium transition-all whitespace-nowrap"
                        >
                            <List size={18} />
                            <span className="text-sm md:text-base">Register</span>
                        </TabsTrigger>
                    </TabsList>
                </div>

                {/* Mobile Tab Dropdown */}
                <div className="md:hidden w-full mb-6">
                    <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                            <Button variant="outline" className="w-full justify-between bg-gray-50 border-gray-200 h-11 rounded-xl">
                                <div className="flex items-center gap-2">
                                    {activeTab === "dashboard" ? <LayoutDashboard size={18} className="text-slate-500" /> : <List size={18} className="text-slate-500" />}
                                    <span className="font-medium text-gray-900 capitalize">
                                        {activeTab}
                                    </span>
                                </div>
                                <ChevronDown size={18} className="text-gray-500" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent className="w-[calc(100vw-3rem)]">
                            <DropdownMenuItem
                                onClick={() => setActiveTab("dashboard")}
                                className={`py-3 flex items-center gap-3 ${activeTab === "dashboard" ? "bg-blue-50 text-blue-600 focus:bg-blue-50 focus:text-blue-600" : ""}`}
                            >
                                <LayoutDashboard size={18} className={activeTab === "dashboard" ? "text-blue-600" : "text-slate-500"} />
                                <span className="font-medium">Dashboard</span>
                            </DropdownMenuItem>
                            <DropdownMenuItem
                                onClick={() => setActiveTab("register")}
                                className={`py-3 flex items-center gap-3 ${activeTab === "register" ? "bg-blue-50 text-blue-600 focus:bg-blue-50 focus:text-blue-600" : ""}`}
                            >
                                <List size={18} className={activeTab === "register" ? "text-blue-600" : "text-slate-500"} />
                                <span className="font-medium">Register</span>
                            </DropdownMenuItem>
                        </DropdownMenuContent>
                    </DropdownMenu>
                </div>

                <TabsContent value="dashboard" className="space-y-8 mt-0 outline-none focus-visible:ring-0">
                    {/* Summary Cards */}
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                        <Card className="bg-white border-slate-100 shadow-sm">
                            <CardContent className="pt-6">
                                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Total</div>
                                <div className="text-2xl font-bold text-slate-900">{stats.total}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-slate-100 shadow-sm">
                            <CardContent className="pt-6">
                                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Active</div>
                                <div className="text-2xl font-bold text-green-600">{stats.active}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-slate-100 shadow-sm">
                            <CardContent className="pt-6">
                                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Expiring Soon</div>
                                <div className="text-2xl font-bold text-orange-600">{stats.expiringSoon}</div>
                            </CardContent>
                        </Card>
                        <Card className="bg-white border-slate-100 shadow-sm">
                            <CardContent className="pt-6">
                                <div className="text-sm font-medium text-muted-foreground uppercase tracking-wider mb-1">Expired</div>
                                <div className="text-2xl font-bold text-red-600">{stats.expired}</div>
                            </CardContent>
                        </Card>
                    </div>

                    {/* Dashboard Table View */}
                    <div className="space-y-4">
                        <QualificationsListView 
                            qualifications={qualifications.filter((q: Qualification) => !q.deleted)} 
                            showStaffColumn={true}
                        />
                    </div>
                </TabsContent>

                <TabsContent value="register" className="space-y-6">
                    <div className="w-full">
                        <TrainingQualificationRegister />
                    </div>
                </TabsContent>
            </Tabs>
        </div>
    );
};

export default QualificationRegisterPage;
